import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getPaymentTerms, getPOs, getPOTriggers, actionTrigger,
  submitMilestone, acceptMilestone, rejectMilestone,
  uploadAttachment, getAttachments,
} from '../services/api';
import { formatCurrency, formatDate, StatusBadge } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircle2, XCircle, Clock, AlertTriangle, ChevronRight,
  Truck, Milestone, Calendar, FileText, Zap, Info, Paperclip, X, Download,
} from 'lucide-react';
import toast from 'react-hot-toast';

const TRIGGER_LABELS = {
  DELIVERY_ACCEPTANCE:  { label: 'Delivery Acceptance', icon: Truck,      color: 'blue'   },
  MILESTONE_ACCEPTANCE: { label: 'Milestone',            icon: Milestone,  color: 'purple' },
  TIME_PERIOD:          { label: 'Time Period',           icon: Calendar,   color: 'amber'  },
  MANUAL:               { label: 'Manual',                icon: Zap,        color: 'gray'   },
};

// ═══════════════════════════════════════════════════════════════════
// MAIN MILESTONES PAGE
// ═══════════════════════════════════════════════════════════════════
export default function Milestones() {
  const [pos, setPOs]               = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [triggers, setTriggers]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [actionInst, setActionInst] = useState(null);
  const navigate  = useNavigate();
  const { canConfirmTrigger, canApproveOrder, user } = useAuth();

  useEffect(() => {
    getPOs().then(r => setPOs(r.data.purchase_orders || [])).catch(() => {});
  }, []);

  const loadTriggers = useCallback(async (poId) => {
    if (!poId) { setTriggers([]); return; }
    setLoading(true);
    try {
      const { data } = await getPOTriggers(poId);
      setTriggers(data.triggers || []);
    } catch { setTriggers([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTriggers(selectedPO); }, [selectedPO, loadTriggers]);

  const handleAction = async (form) => {
    try {
      const res = await actionTrigger(form.installment_id, form);
      toast.success(res.data.message || (form.action_type === 'APPROVE' ? 'Trigger approved' : 'Trigger rejected'));
      setActionInst(null);
      loadTriggers(selectedPO);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  // Group triggers
  const overdue      = triggers.filter(t => t.status === 'OVERDUE' && !t.invoice_id);
  const pending      = triggers.filter(t => ['PENDING','DUE','TRIGGERED'].includes(t.status) && !t.invoice_id && !t.unbilled_id);
  const needsInvoice = triggers.filter(t => t.unbilled_id && !t.invoice_id && !['PAID','CANCELLED','WAIVED'].includes(t.status));
  const completed    = triggers.filter(t => t.invoice_id || ['PAID','CANCELLED','WAIVED'].includes(t.status));
  const hasActionNeeded = overdue.length > 0 || pending.length > 0 || needsInvoice.length > 0;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold" style={{ fontFamily: 'var(--font-display, inherit)' }}>
          Milestones &amp; Triggers
        </h2>
        <p className="text-sm mt-0.5" style={{ color: 'var(--bt-gray-400)' }}>
          Manage delivery acceptance, milestones, and payment triggers per Sales Order.
        </p>
      </div>

      {/* Sales Order selector */}
      <div className="bt-card p-4">
        <label className="bt-label mb-2">Select Sales Order</label>
        <select
          className="bt-select"
          value={selectedPO || ''}
          onChange={e => setSelectedPO(e.target.value || null)}
        >
          <option value="">— Select a Sales Order to view its triggers —</option>
          {pos
            .filter(p => ['APPROVED','CONFIRMED','ACTIVE','COMPLETED'].includes(p.status))
            .map(p => (
              <option key={p.po_id} value={p.po_id}>
                {p.po_number} — {p.buyer_name || p.customer_name || '?'} — {formatCurrency(p.po_value, p.currency)} — {p.status}
              </option>
            ))
          }
        </select>
        {pos.filter(p => ['DRAFT','PENDING'].includes(p.status)).length > 0 && (
          <p className="text-xs mt-2" style={{ color: 'var(--bt-amber)' }}>
            {pos.filter(p => ['DRAFT','PENDING'].includes(p.status)).length} Sales Order(s) in DRAFT/PENDING — approve them first to manage triggers.
          </p>
        )}
      </div>

      {!selectedPO && (
        <div className="bt-card p-12 text-center" style={{ color: 'var(--bt-gray-400)' }}>
          <Milestone size={36} className="mx-auto mb-3" style={{ color: 'var(--bt-gray-300)' }}/>
          <p className="font-medium">Select a Sales Order above to view its triggers</p>
          <p className="text-sm mt-1">Only approved sales orders appear in the list</p>
        </div>
      )}

      {selectedPO && loading && (
        <div className="space-y-3">
          {[1,2].map(i => <div key={i} className="h-20 rounded-xl bt-skeleton"/>)}
        </div>
      )}

      {selectedPO && !loading && triggers.length === 0 && (
        <div className="bt-card p-8 text-center" style={{ color: 'var(--bt-gray-400)' }}>
          <p>No triggers found for this Sales Order.</p>
          <p className="text-xs mt-1">(SO Confirmation triggers fire automatically on approval.)</p>
        </div>
      )}

      {overdue.length > 0 && (
        <TriggerGroup title="Overdue — Action Required" icon={<AlertTriangle size={15}/>}
          headerColor="#DC2626" headerBg="#FEF2F2"
          triggers={overdue} onAction={setActionInst} navigate={navigate}
          canAction={canConfirmTrigger}/>
      )}
      {pending.length > 0 && (
        <TriggerGroup title="Awaiting Action" icon={<Clock size={15}/>}
          headerColor="#D97706" headerBg="#FFFBEB"
          triggers={pending} onAction={setActionInst} navigate={navigate}
          canAction={canConfirmTrigger}/>
      )}
      {needsInvoice.length > 0 && (
        <TriggerGroup title="Unbilled Revenue Raised — Raise Customer Invoice" icon={<FileText size={15}/>}
          headerColor="#2563EB" headerBg="#EFF6FF"
          triggers={needsInvoice} onAction={setActionInst} navigate={navigate}
          canAction={canConfirmTrigger}/>
      )}
      {completed.length > 0 && (
        <TriggerGroup title="Completed" icon={<CheckCircle2 size={15}/>}
          headerColor="#16A34A" headerBg="#F0FDF4"
          triggers={completed} onAction={setActionInst} navigate={navigate}
          canAction={canConfirmTrigger} collapsed={hasActionNeeded}/>
      )}

      {actionInst && (
        <TriggerActionModal
          trigger={actionInst}
          onClose={() => setActionInst(null)}
          onSubmit={handleAction}
        />
      )}
    </div>
  );
}

// ── Trigger group ────────────────────────────────────────────────
function TriggerGroup({ title, icon, triggers, onAction, navigate, headerColor, headerBg, canAction, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);

  return (
    <div className="bt-card overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 border-b"
        style={{ background: headerBg, color: headerColor }}
      >
        <div className="flex items-center gap-2 font-semibold text-sm">
          {icon} {title} ({triggers.length})
        </div>
        <ChevronRight size={15} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}/>
      </button>

      {open && (
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col/>                    {/* Installment */}
            <col style={{width:150}}/>{/* Trigger */}
            <col style={{width:110}}/>{/* Amount */}
            <col style={{width:96}}/>  {/* Due Date */}
            <col style={{width:90}}/>  {/* Status */}
            <col style={{width:160}}/>{/* Invoice / Unbilled */}
            <col style={{width:110}}/>{/* Action */}
          </colgroup>
          <thead>
            <tr>
              {['Installment','Trigger Type','Amount','Due Date','Status','Invoice / Unbilled','Action'].map(h => (
                <th key={h} className="table-th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {triggers.map(t => {
              const meta = TRIGGER_LABELS[t.trigger_type] || { label: t.trigger_type, icon: Info, color: 'gray' };
              const Icon = meta.icon;
              return (
                <tr key={t.installment_id} className="hover:bg-gray-50">
                  <td className="table-td font-medium">{t.label}</td>
                  <td className="table-td">
                    <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: `var(--bt-${meta.color === 'purple' ? 'blue' : meta.color})` }}>
                      <Icon size={12}/> {meta.label}
                    </span>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--bt-gray-400)' }}>{t.term_name}</div>
                  </td>
                  <td className="table-td font-semibold">{formatCurrency(t.computed_amount, t.currency)}</td>
                  <td className="table-td text-sm" style={{ color: 'var(--bt-gray-500)' }}>{formatDate(t.due_date) || '—'}</td>
                  <td className="table-td"><StatusBadge status={t.status}/></td>
                  <td className="table-td text-xs">
                    {t.invoice_id && (
                      <button onClick={() => navigate(`/invoices/${t.invoice_id}`)}
                        className="font-mono hover:underline" style={{ color: 'var(--bt-orange)' }}>
                        {t.invoice_number || 'View Invoice'}
                      </button>
                    )}
                    {t.unbilled_id && !t.invoice_id && (
                      <div className="space-y-1">
                        <button onClick={() => navigate(`/unbilled-revenue/${t.unbilled_id}`)}
                          className="hover:underline block" style={{ color: 'var(--bt-blue)' }}>
                          Unbilled entry ↗
                        </button>
                        {canAction && (
                          <button onClick={() => onAction(t)}
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded font-medium"
                            style={{ background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0' }}>
                            <Zap size={10}/> Raise Invoice
                          </button>
                        )}
                      </div>
                    )}
                    {!t.invoice_id && !t.unbilled_id && <span style={{ color: 'var(--bt-gray-300)' }}>—</span>}
                  </td>
                  <td className="table-td">
                    {canAction && !['PAID','CANCELLED','WAIVED'].includes(t.status) && !t.invoice_id ? (
                      <button onClick={() => onAction(t)}
                        className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg font-medium"
                        style={{
                          background: t.unbilled_id ? '#F0FDF4' : 'var(--bt-orange-light)',
                          color: t.unbilled_id ? '#16A34A' : 'var(--bt-orange)',
                          border: `1px solid ${t.unbilled_id ? '#BBF7D0' : 'var(--bt-orange-border)'}`,
                        }}>
                        <Zap size={11}/> {t.unbilled_id ? 'Raise Invoice' : 'Action'}
                      </button>
                    ) : (
                      <span style={{ color: 'var(--bt-gray-300)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Trigger Action Modal (with file attachment) ──────────────────
function TriggerActionModal({ trigger, onClose, onSubmit }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    installment_id: trigger.installment_id,
    action_type:    'APPROVE',
    remarks:        '',
    delivery_date:  trigger.trigger_type === 'DELIVERY_ACCEPTANCE' ? today : '',
    document_ref:   '',
  });
  const [saving, setSaving]         = useState(false);
  const [attachments, setAttachments] = useState([]);  // files staged for upload
  const [uploading, setUploading]   = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isDelivery  = trigger.trigger_type === 'DELIVERY_ACCEPTANCE';
  const isMilestone = trigger.trigger_type === 'MILESTONE_ACCEPTANCE';

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAttachments(prev => [...prev, {
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_data: ev.target.result, // base64
          preview:   file.type.startsWith('image/') ? ev.target.result : null,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // 1. Submit the trigger action
      const res = await onSubmit(form);

      // 2. Upload any attachments to the installment entity
      if (attachments.length > 0) {
        setUploading(true);
        for (const att of attachments) {
          try {
            await uploadAttachment({
              entity_type: 'installment',
              entity_id:   trigger.installment_id,
              file_name:   att.file_name,
              file_type:   att.file_type,
              file_size:   att.file_size,
              file_data:   att.file_data,
            });
          } catch (uploadErr) {
            toast.error(`Failed to upload ${att.file_name}`);
          }
        }
        setUploading(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bt-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div className="bt-modal w-full" style={{ maxWidth: 560 }} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="bt-modal-header">
          <div>
            <h3 className="bt-modal-title">Action: {trigger.label}</h3>
            <p className="text-sm mt-0.5" style={{ color: 'var(--bt-gray-500)' }}>
              {trigger.trigger_type?.replace(/_/g,' ')} · {formatCurrency(trigger.computed_amount, trigger.currency)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="bt-modal-close" aria-label="Close">
            <X size={20}/>
          </button>
        </div>

        <div className="bt-modal-body space-y-4">
          {/* What will happen info box */}
          <div className="bt-alert bt-alert-info text-xs">
            <div>
              <p className="font-medium mb-1">{form.action_type === 'APPROVE' ? 'On Approve:' : 'On Reject:'}</p>
              {form.action_type === 'APPROVE' ? (
                <ul className="list-disc list-inside space-y-0.5">
                  {trigger.invoice_id && <li>Invoice already raised — trigger recorded only</li>}
                  {trigger.unbilled_id && !trigger.invoice_id && <>
                    <li className="font-medium">Customer invoice raised (Step O)</li>
                    <li className="font-mono">Dr. Customer A/c / Cr. Unbilled Revenue A/c</li>
                  </>}
                  {!trigger.invoice_id && !trigger.unbilled_id && <>
                    <li>Invoice raised for {formatCurrency(trigger.computed_amount, trigger.currency)}</li>
                    {isDelivery && <li>Sales Order status → ACTIVE</li>}
                  </>}
                </ul>
              ) : (
                <p>Installment remains PENDING. No invoice raised.</p>
              )}
            </div>
          </div>

          {/* Approve / Reject */}
          <div>
            <label className="bt-label">Decision</label>
            <div className="flex gap-3">
              {['APPROVE','REJECT'].map(opt => (
                <label key={opt} className="flex-1 flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors"
                  style={{
                    borderColor: form.action_type === opt ? (opt === 'APPROVE' ? '#4ADE80' : '#F87171') : 'var(--bt-gray-200)',
                    background:  form.action_type === opt ? (opt === 'APPROVE' ? '#F0FDF4' : '#FEF2F2') : '#fff',
                  }}>
                  <input type="radio" name="action" value={opt}
                    checked={form.action_type === opt} onChange={() => set('action_type', opt)}/>
                  {opt === 'APPROVE'
                    ? <><CheckCircle2 size={15} style={{ color: '#16A34A' }}/><span className="text-sm font-medium" style={{ color: '#16A34A' }}>Approve</span></>
                    : <><XCircle size={15} style={{ color: '#DC2626' }}/><span className="text-sm font-medium" style={{ color: '#DC2626' }}>Reject</span></>
                  }
                </label>
              ))}
            </div>
          </div>

          {/* Delivery date */}
          {isDelivery && form.action_type === 'APPROVE' && (
            <div>
              <label className="bt-label">Actual Delivery Date <span style={{ color: 'var(--bt-red)' }}>*</span></label>
              <input type="date" className="bt-input" value={form.delivery_date}
                onChange={e => set('delivery_date', e.target.value)}/>
              <p className="text-xs mt-1" style={{ color: 'var(--bt-gray-400)' }}>Date goods/services were actually delivered</p>
            </div>
          )}

          {/* Document reference */}
          <div>
            <label className="bt-label">
              Document Reference
              {isDelivery && ' (Delivery Challan, GRN No., etc.)'}
              {isMilestone && ' (UAT sign-off, Completion cert., etc.)'}
            </label>
            <input className="bt-input" value={form.document_ref}
              onChange={e => set('document_ref', e.target.value)}
              placeholder={isDelivery ? 'e.g. DC-2026-1042 / GRN-456' : isMilestone ? 'e.g. UAT-CERT-2026-001' : 'Reference number'}/>
          </div>

          {/* ── File Attachments ─────────────────────────────────── */}
          <div>
            <label className="bt-label">Supporting Documents</label>
            <div
              onClick={() => fileRef.current?.click()}
              className="flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
              style={{ borderColor: 'var(--bt-gray-300)', background: 'var(--bt-gray-50)' }}
              onMouseOver={e => e.currentTarget.style.borderColor = 'var(--bt-orange)'}
              onMouseOut={e => e.currentTarget.style.borderColor = 'var(--bt-gray-300)'}
            >
              <Paperclip size={18} style={{ color: 'var(--bt-gray-400)' }}/>
              <div className="text-sm" style={{ color: 'var(--bt-gray-500)' }}>
                <span className="font-medium" style={{ color: 'var(--bt-orange)' }}>Click to attach files</span>
                {' '}or drag &amp; drop
              </div>
              <span className="text-xs" style={{ color: 'var(--bt-gray-300)' }}>PDF, JPG, PNG up to 5MB each</span>
            </div>
            <input ref={fileRef} type="file" multiple className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              onChange={handleFileSelect}/>

            {/* Staged files list */}
            {attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {attachments.map((att, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded-lg border"
                    style={{ borderColor: 'var(--bt-gray-200)', background: '#fff' }}>
                    {att.preview
                      ? <img src={att.preview} alt="" className="w-8 h-8 rounded object-cover"/>
                      : <div className="w-8 h-8 rounded flex items-center justify-center"
                          style={{ background: 'var(--bt-orange-light)' }}>
                          <FileText size={14} style={{ color: 'var(--bt-orange)' }}/>
                        </div>
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{att.file_name}</p>
                      <p className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>
                        {(att.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button onClick={() => removeAttachment(idx)}
                      style={{ color: 'var(--bt-gray-400)', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <X size={14}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Remarks */}
          <div>
            <label className="bt-label">Remarks</label>
            <textarea className="bt-textarea" rows={3} value={form.remarks}
              onChange={e => set('remarks', e.target.value)}
              placeholder={form.action_type === 'APPROVE' ? 'Add notes about this approval…' : 'Reason for rejection'}/>
          </div>
        </div>

        <div className="bt-modal-footer">
          <button onClick={onClose} className="bt-btn bt-btn-secondary">Cancel</button>
          <button onClick={handleSubmit}
            disabled={saving || uploading || (isDelivery && form.action_type === 'APPROVE' && !form.delivery_date)}
            className="bt-btn"
            style={{
              background: form.action_type === 'APPROVE' ? '#16A34A' : '#DC2626',
              color: '#fff',
              opacity: (saving || uploading) ? 0.7 : 1,
            }}>
            {form.action_type === 'APPROVE'
              ? <><CheckCircle2 size={14}/> {uploading ? 'Uploading…' : saving ? 'Approving…' : `Approve${attachments.length > 0 ? ` + ${attachments.length} file(s)` : ''}`}</>
              : <><XCircle size={14}/> {saving ? 'Rejecting…' : 'Reject'}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
